import { create } from 'zustand';
import { SystemConfig, RateLimitConfig, NotificationSettings, EmailConfiguration, SmsConfiguration } from '../types';
import configurationApi from '../services/api/configurationApi';
import logger from '../services/loggerService';
import {
  getStoredGeneralConfig,
  persistGeneralConfig,
  resolveNotifications,
} from '../services/systemConfigCacheService';
import {
  INITIAL_RATE_LIMIT_CONFIG,
  RATE_LIMIT_VERSION_KEY,
  SYSTEM_CONFIG_CTX,
} from '../constants/systemConfigConstants';
import { ZustandOptimisticAdapter } from '../services/optimistic/ZustandOptimisticAdapter';
import { ReconciliationProtocol } from '../services/optimistic/ReconciliationProtocol';
import { OptimisticAction } from '../types/optimistic.types';
import { BackendSystemConfig, SystemConfigState } from '../types/systemConfig.types';

// ── Initial state ─────────────────────────────────────────────────────────────

const INITIAL_SYSTEM_CONFIG: Omit<SystemConfig, 'payment' | 'optimistic'> = {
  general: getStoredGeneralConfig(),
  financial: {
    lateFeePolicy: {
      enableAutomaticLateFees:  false,
      gracePeriodDays:          0,
      calculationMethod:        'FIXED_AMOUNT',
      feeValue:                 0,
      frequency:                'ONE_TIME',
    },
    registrationBlockPolicy: {
      enableAutomaticBlocking:          false,
      blockAfterMissedInstallments:     1,
      blockIfOutstandingBalanceExceeds: 0,
      notificationMessage:              '',
    },
    paymentReminderPolicy: {
      enablePaymentReminders:     false,
      reminderDaysBeforeDue:      '3',
      reminderWorkerSchedule:     '0 8 * * *',
      reminderBatchSize:          100,
      enableInstallmentReminders: false,
    },
  },
  security:     {},
  maintenance:  { maintenanceModeEnabled: false },
  notifications: {
    emailConfig: { defaultSenderEmail: '', testStatus: 'idle' },
    smsConfig:   { testStatus: 'idle' },
    templates:   [],
  },
};

// ── Notification hard defaults (explicitly typed — eliminates undefined) ─────
const HARD_DEFAULT_EMAIL_CONFIG: EmailConfiguration = { defaultSenderEmail: '', testStatus: 'idle' };
const HARD_DEFAULT_SMS_CONFIG:   SmsConfiguration   = { testStatus: 'idle' };

// ── Store ─────────────────────────────────────────────────────────────────────

export const useSystemConfigStore = create<SystemConfigState>((set, get) => {
  const adapter  = new ZustandOptimisticAdapter<SystemConfigState>(get, set);
  const protocol = new ReconciliationProtocol();

  // No function needed — explicit typed constants guarantee non-undefined at call sites

  return {
    ...INITIAL_SYSTEM_CONFIG as unknown as SystemConfigState,
    optimistic:      adapter,
    rateLimitConfig: null,
    configVersions:  {},
    isLoading:       true,
    error:           null,
    emailTestStatus: 'idle',
    smsTestStatus:   'idle',

    setEmailTestStatus: (status) => set({ emailTestStatus: status }),
    setSmsTestStatus:   (status) => set({ smsTestStatus: status }),

    // ── PUBLIC CONFIG ─────────────────────────────────────────────────────────
    fetchPublicConfig: async (signal?: AbortSignal) => {
      set({ isLoading: true, error: null });
      try {
        const publicData = await configurationApi.getPublicConfig(signal);

        set((state) => {
          const updatedGeneral = { ...state.general, ...publicData };
          persistGeneralConfig(updatedGeneral as Record<string, unknown>);
          return { general: updatedGeneral, isLoading: false };
        });
      } catch (error: unknown) {
        if (error instanceof Error && error.name === 'AbortError') {
          logger.debug('Public config fetch aborted', { context: SYSTEM_CONFIG_CTX.PUB });
          set({ isLoading: false });
          return;
        }
        const errorMessage = error instanceof Error
          ? error.message
          : 'Failed to load public system configuration';
        logger.error('Failed to fetch public system config', { error }, SYSTEM_CONFIG_CTX.PUB);
        // Cached branding stays visible; expose error for UI retry
        set({ isLoading: false, error: errorMessage });
      }
    },

    // ── ADMIN CONFIG ──────────────────────────────────────────────────────────
    fetchAdminConfig: async () => {
      set({ isLoading: true, error: null });
      try {
        const config = await configurationApi.getAdminConfig() ?? {};
        const safeNotifications = config.notifications ?? {};
        const { emailConfig, smsConfig } = resolveNotifications(
          safeNotifications,
          get().notifications?.emailConfig,
          get().notifications?.smsConfig,
          HARD_DEFAULT_EMAIL_CONFIG,
          HARD_DEFAULT_SMS_CONFIG,
        );

        const updatedGeneral = config.general ?? getStoredGeneralConfig();
        persistGeneralConfig(updatedGeneral as Record<string, unknown>);

        // ── UI unblocked immediately; rate limit is secondary fire-and-forget ──
        set({
          general:      updatedGeneral,
          financial:    config.financial   ?? INITIAL_SYSTEM_CONFIG.financial,
          security:     config.security    ?? INITIAL_SYSTEM_CONFIG.security,
          maintenance:  config.maintenance ?? INITIAL_SYSTEM_CONFIG.maintenance,
          notifications: { emailConfig, smsConfig, templates: [] },
          emailTestStatus: (emailConfig as { testStatus?: 'idle' | 'passed' | 'failed' }).testStatus ?? 'idle',
          smsTestStatus:   (smsConfig   as { testStatus?: 'idle' | 'passed' | 'failed' }).testStatus ?? 'idle',
          configVersions: { ...get().configVersions, ...(config.versions ?? {}) },
          isLoading: false,
        });

        // Fire-and-forget — failure falls back to safe defaults silently
        configurationApi.getRateLimitConfig()
          .then((rateLimitData) => {
            set((state) => ({
              rateLimitConfig: rateLimitData,
              configVersions: {
                ...state.configVersions,
                [RATE_LIMIT_VERSION_KEY]: rateLimitData.version ?? 0,
              },
            }));
          })
          .catch((rlError: unknown) => {
            logger.warn('Failed to fetch rate limit config — applying safe defaults', {
              error: rlError,
              context: SYSTEM_CONFIG_CTX.ADMIN,
            });
            set({ rateLimitConfig: INITIAL_RATE_LIMIT_CONFIG });
          });

      } catch (error: unknown) {
        const errorMessage = error instanceof Error
          ? error.message
          : 'Failed to load admin configuration';
        logger.error('Failed to fetch admin system config', { error }, SYSTEM_CONFIG_CTX.ADMIN);
        set({ error: errorMessage, isLoading: false });
      }
    },

    // ── UPDATE CONFIG (optimistic + OCC) ──────────────────────────────────────
    updateConfig: async (newConfig: Partial<Omit<SystemConfig, 'payment'>>, reason: string) => {
      const optimistic = get().optimistic;

      const action: OptimisticAction<SystemConfigState, Partial<Omit<SystemConfig, 'payment'>>> = {
        type: 'UPDATE_SYSTEM_CONFIG',
        payload: newConfig,
        apply: (state, payload) => ({
          ...state,
          ...payload,
          notifications: {
            ...state.notifications,
            ...(payload.notifications ?? {}),
          } as NotificationSettings,
        }),
        rollback: (_, snapshot) => snapshot,
      };

      const mutationId = optimistic.applyOptimisticUpdate(action);

      try {
        const response = await configurationApi.updateAdminConfig(
          newConfig,
          reason,
          get().configVersions,
        );

        const result = protocol.interpretResponse<{ config: BackendSystemConfig; versions?: Record<string, number> }>(response);

        if (result.success && result.data) {
          const { config: updatedConfig, versions } = result.data;
          const safeNotifications = updatedConfig.notifications ?? {};
          const { emailConfig, smsConfig } = resolveNotifications(
            safeNotifications,
            get().notifications?.emailConfig,
            get().notifications?.smsConfig,
            HARD_DEFAULT_EMAIL_CONFIG,
            HARD_DEFAULT_SMS_CONFIG,
          );

          const updatedGeneral = updatedConfig.general ?? get().general;
          persistGeneralConfig(updatedGeneral as Record<string, unknown>);

          set((state) => ({
            ...state,
            ...updatedConfig,
            general:      { ...state.general, ...updatedGeneral },
            notifications: { emailConfig, smsConfig, templates: [] } as NotificationSettings,
            emailTestStatus: (emailConfig as { testStatus?: 'idle' | 'passed' | 'failed' }).testStatus ?? 'idle',
            smsTestStatus:   (smsConfig   as { testStatus?: 'idle' | 'passed' | 'failed' }).testStatus ?? 'idle',
            configVersions:  { ...state.configVersions, ...(versions ?? {}) },
            isLoading: false,
          }));

          optimistic.commitMutation(mutationId);
          logger.debug('Configuration reconciled successfully', { mutationId, context: SYSTEM_CONFIG_CTX.UPDATE });
        } else {
          throw result.error ?? new Error('Reconciliation protocol failure');
        }
      } catch (error: unknown) {
        const result = protocol.interpretResponse(error);

        if (result.conflictDetected) {
          logger.warn('Configuration conflict detected — rolling back optimistic state', {
            mutationId,
            context: SYSTEM_CONFIG_CTX.UPDATE,
          });
        } else {
          logger.error('Optimistic update failed — rolling back', { error }, SYSTEM_CONFIG_CTX.UPDATE);
        }

        optimistic.rollbackMutation(mutationId);
        set({
          error: error instanceof Error ? error.message : 'Update failed. Please try again.',
          isLoading: false,
        });
        throw error;
      }
    },

    // ── RECONCILE WITH SERVER STATE ───────────────────────────────────────────
    reconcileWithServerState: (serverState: Partial<BackendSystemConfig>) => {
      const safeNotifications = serverState.notifications ?? {};
      const { emailConfig, smsConfig } = resolveNotifications(
        safeNotifications,
        get().notifications?.emailConfig,
        get().notifications?.smsConfig,
        HARD_DEFAULT_EMAIL_CONFIG,
        HARD_DEFAULT_SMS_CONFIG,
      );

      const updatedGeneral = serverState.general ?? get().general;
      persistGeneralConfig(updatedGeneral as Record<string, unknown>);

      set((state) => ({
        ...state,
        ...serverState,
        general: { ...state.general, ...updatedGeneral },
        notifications: {
          ...state.notifications,
          ...(safeNotifications.email ?? safeNotifications.emailConfig ? { emailConfig } : {}),
          ...(safeNotifications.sms   ?? safeNotifications.smsConfig   ? { smsConfig }   : {}),
        },
      }));

      // Advance optimistic base snapshot to the new stable server state
      get().optimistic.reconcileWithServerState(get());
    },

    // ── UPDATE RATE LIMIT CONFIG (true optimistic) ────────────────────────────
    updateRateLimitConfig: async (
      config: Partial<RateLimitConfig>,
      reason: string,
      elevationToken?: string,
    ) => {
      const previousConfig = get().rateLimitConfig;
      const currentVersion = get().configVersions[RATE_LIMIT_VERSION_KEY] ?? 0;
      const currentConfig  = get().rateLimitConfig ?? INITIAL_RATE_LIMIT_CONFIG;

      // True optimistic update — apply state before the API call
      set((state) => ({
        rateLimitConfig: { ...currentConfig, ...config },
        configVersions: {
          ...state.configVersions,
          [RATE_LIMIT_VERSION_KEY]: currentVersion + 1,
        },
      }));

      try {
        await configurationApi.updateRateLimitConfig(config, reason, elevationToken, currentVersion);
        logger.debug('Rate limit config updated and committed', { context: SYSTEM_CONFIG_CTX.RL });
      } catch (error: unknown) {
        // Rollback — restore both the config AND the OCC version
        logger.error('Failed to update rate limit config — rolling back optimistic state', { error }, SYSTEM_CONFIG_CTX.RL);
        set((state) => ({
          rateLimitConfig: previousConfig,
          configVersions: {
            ...state.configVersions,
            [RATE_LIMIT_VERSION_KEY]: currentVersion,
          },
          error: error instanceof Error
            ? error.message
            : 'Failed to update rate limits. Please try again.',
        }));
        throw error;
      }
    },
  };
});
